import { useEffect, useState } from 'react'
import { Spinner } from '../../../components/ui/Spinner'
import { Modal } from '../../../components/ui/Modal'
import { inputClass, selectClass } from '../../../components/ui/FormField'
import { api } from '../../../api/axios'
import { DataBR } from '../../../components/ui/DataBR'
import { useLeiauteColunas } from '../../../hooks/useLeiauteColunas'
import { ColunaArrastavel, MenuLeiaute } from '../../../components/ui/LeiauteColunas'
import { dataLocalISO } from '../../../lib/dataLocal'

// Contas a Pagar / Contas a Receber (migration 018): lancamentos manuais, baixa e anexos
// (comprovante de pagamento, copia da nota). Anexos vao em base64 para a API e so abrem logado.

export type TipoConta = 'pagar' | 'receber'
type Status = 'aberta' | 'vencida' | 'paga' | 'cancelada'

interface Conta {
  idconta: number
  tipo: TipoConta
  descricao: string
  pessoa: string | null
  documento: string | null
  categoria: string | null
  valor: number
  vencimento: string
  idcampeonato: number | null
  idetapa: number | null
  situacao: 'aberta' | 'paga' | 'cancelada'
  status: Status
  data_pagamento: string | null
  valor_pago: number | null
  forma_pagamento: string | null
  observacao: string | null
  campeonato_descricao: string | null
  qtd_anexos: number
  qtd_comprovantes: number
  qtd_notas: number
}

interface Anexo { idanexo: number; tipo: 'comprovante' | 'nota' | 'outro'; nome_original: string; mime: string; tamanho: number; enviado_em: string; enviado_por_nome: string | null }
interface Totais { em_aberto: number; qtd_em_aberto: number; vencido: number; qtd_vencidas: number; pago_mes: number }

const TXT = {
  pagar: { pessoa: 'Fornecedor', baixar: 'Pagar', paga: 'Paga', titulo: 'Contas a Pagar', pagoMes: 'Pago no mes', novo: '+ Nova conta a pagar' },
  receber: { pessoa: 'Cliente / pagador', baixar: 'Receber', paga: 'Recebida', titulo: 'Contas a Receber', pagoMes: 'Recebido no mes', novo: '+ Nova conta a receber' },
}
const CATEGORIAS = {
  pagar: ['Premiacao', 'Alimentacao', 'Material', 'Municao', 'Alvos', 'Transporte', 'Aluguel', 'Taxas', 'Servicos', 'Outras'],
  receber: ['Patrocinio', 'Apoio', 'Inscricoes avulsas', 'Venda de municao', 'Venda de material', 'Mensalidade', 'Outras'],
}
const FORMAS: Array<[string, string]> = [['pix', 'PIX'], ['dinheiro', 'Dinheiro'], ['cartao', 'Cartao'], ['boleto', 'Boleto'], ['transferencia', 'Transferencia'], ['outro', 'Outro']]
// o "comprovante" e o mesmo tipo no banco; o nome muda conforme a aba (pagamento x recebimento)
const TIPO_ANEXO = {
  pagar: { comprovante: 'Comprovante de pagamento', nota: 'Copia da nota', outro: 'Outro documento' },
  receber: { comprovante: 'Comprovante de recebimento', nota: 'Copia da nota', outro: 'Outro documento' },
}
const TAM_MAX = 10 * 1024 * 1024
const ACEITA = '.pdf,.jpg,.jpeg,.png,.xml,application/pdf,image/jpeg,image/png,text/xml,application/xml'

const brl = (v: number | null | undefined) => (v ?? 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
const dataBR = (iso: string | null) => (iso ? iso.slice(0, 10).split('-').reverse().join('/') : '—')
const hoje = () => dataLocalISO()
// "1.234,56" / "150,5" / "150.50" -> numero
function paraNumero(v: string): number | null {
  const t = v.trim()
  if (!t) return null
  const n = Number(t.includes(',') ? t.replace(/\./g, '').replace(',', '.') : t)
  return Number.isFinite(n) ? n : null
}
// erros lancados na API vem com o texto em `message` (e `error` = "Bad Request"/"Conflict"...)
const erroApi = (err: any, padrao: string) => err?.response?.data?.message || err?.response?.data?.error || padrao

function corStatus(s: Status) {
  return s === 'paga' ? 'bg-green-100 text-green-700' : s === 'vencida' ? 'bg-red-100 text-red-700'
    : s === 'cancelada' ? 'bg-gray-200 text-gray-600' : 'bg-amber-100 text-amber-700'
}

function lerBase64(f: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader()
    r.onload = () => resolve(String(r.result))
    r.onerror = () => reject(new Error('Nao foi possivel ler o arquivo'))
    r.readAsDataURL(f)
  })
}

async function enviarAnexo(idconta: number, tipo: string, arquivo: File) {
  if (arquivo.size > TAM_MAX) throw new Error(`"${arquivo.name}" passa de 10 MB`)
  const conteudo = await lerBase64(arquivo)
  await api.post(`/financeiro/contas/${idconta}/anexos`, { tipo, nome: arquivo.name, conteudo })
}

async function abrirAnexo(a: Anexo) {
  const resp = await api.get<Blob>(`/financeiro/anexos/${a.idanexo}`, { responseType: 'blob' })
  // XML sempre como download (aberto no navegador poderia executar conteudo ativo)
  const blob = a.mime === 'application/xml' ? new Blob([resp.data], { type: 'application/octet-stream' }) : resp.data
  const url = URL.createObjectURL(blob)
  if (a.mime === 'application/xml') {
    const link = document.createElement('a')
    link.href = url; link.download = a.nome_original
    document.body.appendChild(link); link.click(); document.body.removeChild(link)
  } else {
    window.open(url, '_blank', 'noopener')
  }
  setTimeout(() => URL.revokeObjectURL(url), 60000)
}

// sem campeonato: as contas sao da empresa (clube); despesas de campeonato continuam na aba Despesas.
// Contas antigas que tinham campeonato mantem o vinculo no banco (a tela nao envia o campo).
// colunas da lista (a de acoes fica sempre no fim e nao entra no leiaute)
const COLUNAS_PADRAO = ['vencimento', 'descricao', 'pessoa', 'valor', 'situacao', 'anexos']

const vazio = { descricao: '', pessoa: '', documento: '', categoria: '', valor: '', vencimento: hoje(), observacao: '' }

export function Contas({ tipo, onAlert }: { tipo: TipoConta; onAlert: (a: { type: 'success' | 'error'; message: string }) => void }) {
  const T = TXT[tipo]
  const [filtroStatus, setFiltroStatus] = useState('em_aberto')
  const [de, setDe] = useState('')
  const [ate, setAte] = useState('')
  const [busca, setBusca] = useState('')
  const [origem, setOrigem] = useState('')
  const [contas, setContas] = useState<Conta[]>([])
  const [totais, setTotais] = useState<Totais | null>(null)
  const [carregando, setCarregando] = useState(false)
  const leiaute = useLeiauteColunas(`contas-${tipo}`, COLUNAS_PADRAO)

  const [editando, setEditando] = useState<Conta | null>(null)
  const [formAberto, setFormAberto] = useState(false)
  const [form, setForm] = useState({ ...vazio })
  const [anexos, setAnexos] = useState<Anexo[]>([])
  const [tipoAnexo, setTipoAnexo] = useState<'comprovante' | 'nota' | 'outro'>('nota')
  const [salvando, setSalvando] = useState(false)

  const [baixando, setBaixando] = useState<Conta | null>(null)
  const [baixa, setBaixa] = useState({ data_pagamento: hoje(), valor_pago: '', forma_pagamento: 'pix' })
  const [comprovanteBaixa, setComprovanteBaixa] = useState<File | null>(null)

  async function carregar() {
    setCarregando(true)
    try {
      const params: Record<string, string> = { tipo }
      if (filtroStatus) params.status = filtroStatus
      if (de) params.de = de
      if (ate) params.ate = ate
      if (busca.trim()) params.busca = busca.trim()
      if (origem) params.referencia = origem
      const { data } = await api.get<{ data: Conta[]; totais: Totais }>('/financeiro/contas', { params })
      setContas(data.data)
      setTotais(data.totais)
    } catch (err) {
      onAlert({ type: 'error', message: erroApi(err, 'Erro ao carregar as contas') })
    } finally { setCarregando(false) }
  }

  useEffect(() => { carregar() }, [tipo, filtroStatus, de, ate, origem])
  useEffect(() => { const t = setTimeout(carregar, 400); return () => clearTimeout(t) }, [busca])

  async function carregarAnexos(id: number) {
    const { data } = await api.get<{ data: Conta & { anexos: Anexo[] } }>(`/financeiro/contas/${id}`)
    setAnexos(data.data.anexos)
    return data.data
  }

  function novo() {
    setEditando(null); setAnexos([]); setForm({ ...vazio }); setTipoAnexo('nota'); setFormAberto(true)
  }

  async function abrir(c: Conta) {
    setEditando(c)
    setForm({
      descricao: c.descricao, pessoa: c.pessoa ?? '', documento: c.documento ?? '', categoria: c.categoria ?? '',
      valor: String(c.valor).replace('.', ','), vencimento: c.vencimento,
      observacao: c.observacao ?? '',
    })
    setTipoAnexo(c.situacao === 'paga' ? 'comprovante' : 'nota')
    setFormAberto(true)
    try { await carregarAnexos(c.idconta) } catch { setAnexos([]) }
  }

  async function salvar() {
    setSalvando(true)
    try {
      const corpo = { ...form, tipo, valor: paraNumero(form.valor) }
      if (editando) {
        const { data } = await api.put<{ data: Conta }>(`/financeiro/contas/${editando.idconta}`, corpo)
        setEditando(data.data)
        onAlert({ type: 'success', message: 'Conta atualizada.' })
      } else {
        const { data } = await api.post<{ data: Conta }>('/financeiro/contas', corpo)
        setEditando(data.data)
        setAnexos([])
        onAlert({ type: 'success', message: 'Conta cadastrada. Agora voce pode anexar a nota.' })
      }
      carregar()
    } catch (err) {
      onAlert({ type: 'error', message: erroApi(err, 'Erro ao salvar a conta') })
    } finally { setSalvando(false) }
  }

  async function anexar(lista: FileList | null) {
    // copia antes: o campo de arquivo e limpo logo depois (e o FileList esvazia junto)
    const arquivos = Array.from(lista ?? [])
    if (!editando || !arquivos.length) return
    setSalvando(true)
    try {
      for (const f of arquivos) await enviarAnexo(editando.idconta, tipoAnexo, f)
      await carregarAnexos(editando.idconta)
      onAlert({ type: 'success', message: `${arquivos.length} anexo(s) enviado(s).` })
      carregar()
    } catch (err: any) {
      onAlert({ type: 'error', message: erroApi(err, err?.message || 'Erro ao enviar o anexo') })
    } finally { setSalvando(false) }
  }

  async function excluirAnexo(a: Anexo) {
    if (!editando || !window.confirm(`Excluir o anexo "${a.nome_original}"?`)) return
    try {
      await api.delete(`/financeiro/anexos/${a.idanexo}`)
      await carregarAnexos(editando.idconta)
      carregar()
    } catch (err) { onAlert({ type: 'error', message: erroApi(err, 'Erro ao excluir o anexo') }) }
  }

  async function ver(a: Anexo) {
    try { await abrirAnexo(a) } catch (err) { onAlert({ type: 'error', message: erroApi(err, 'Erro ao abrir o anexo') }) }
  }

  function iniciarBaixa(c: Conta) {
    setBaixando(c)
    setBaixa({ data_pagamento: hoje(), valor_pago: String(c.valor).replace('.', ','), forma_pagamento: 'pix' })
    setComprovanteBaixa(null)
  }

  async function confirmarBaixa() {
    if (!baixando) return
    setSalvando(true)
    try {
      await api.post(`/financeiro/contas/${baixando.idconta}/baixar`, { ...baixa, valor_pago: paraNumero(baixa.valor_pago) })
      if (comprovanteBaixa) await enviarAnexo(baixando.idconta, 'comprovante', comprovanteBaixa)
      onAlert({ type: 'success', message: `Conta ${tipo === 'pagar' ? 'paga' : 'recebida'}${comprovanteBaixa ? ' com comprovante anexado' : ''}.` })
      setBaixando(null)
      carregar()
    } catch (err: any) {
      onAlert({ type: 'error', message: erroApi(err, err?.message || 'Erro ao dar baixa') })
    } finally { setSalvando(false) }
  }

  async function acao(c: Conta, qual: 'reabrir' | 'cancelar' | 'excluir') {
    const pergunta = { reabrir: 'Reabrir esta conta (desfaz o pagamento/cancelamento)?', cancelar: 'Cancelar esta conta?', excluir: 'Excluir esta conta e todos os anexos? Nao da para desfazer.' }[qual]
    if (!window.confirm(pergunta)) return
    try {
      if (qual === 'excluir') await api.delete(`/financeiro/contas/${c.idconta}`)
      else await api.post(`/financeiro/contas/${c.idconta}/${qual}`)
      if (qual === 'excluir') setFormAberto(false)
      carregar()
    } catch (err) { onAlert({ type: 'error', message: erroApi(err, 'Erro ao executar a acao') }) }
  }

  const TITULO_COLUNA: Record<string, string> = {
    vencimento: 'Vencimento', descricao: 'Descricao', pessoa: T.pessoa, valor: 'Valor', situacao: 'Situacao', anexos: 'Anexos',
  }

  function celula(id: string, c: Conta) {
    switch (id) {
      case 'vencimento': return <td key={id} className="px-3 py-2 whitespace-nowrap">{dataBR(c.vencimento)}</td>
      case 'descricao': return (
        <td key={id} className="px-3 py-2">
          <p className="text-compumaq-texto">{c.descricao}</p>
          <p className="text-xs text-compumaq-textoSuave">{[c.categoria, c.documento && `Doc ${c.documento}`].filter(Boolean).join(' · ')}</p>
        </td>
      )
      case 'pessoa': return <td key={id} className="px-3 py-2 text-compumaq-textoSuave">{c.pessoa ?? '—'}</td>
      case 'valor': return (
        <td key={id} className="px-3 py-2 text-right whitespace-nowrap">
          {brl(c.valor)}
          {c.situacao === 'paga' && <p className="text-xs text-green-700">{dataBR(c.data_pagamento)} · {brl(c.valor_pago)}</p>}
        </td>
      )
      case 'situacao': return <td key={id} className="px-3 py-2"><span className={`text-xs px-2 py-0.5 rounded-full font-medium ${corStatus(c.status)}`}>{rotuloStatus(c.status)}</span></td>
      case 'anexos': return (
        <td key={id} className="px-3 py-2 text-xs text-compumaq-textoSuave whitespace-nowrap">
          {c.qtd_anexos ? `📎 ${c.qtd_notas} nota · ${c.qtd_comprovantes} comprov.` : '—'}
        </td>
      )
      default: return null
    }
  }

  const rotuloStatus = (s: Status) => (s === 'paga' ? T.paga : s === 'vencida' ? 'Vencida' : s === 'cancelada' ? 'Cancelada' : 'Em aberto')

  return (
    <div className="space-y-4" data-contas={tipo}>
      {totais && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="bg-compumaq-pretoSuave rounded-xl border border-compumaq-dourado/25 p-4">
            <p className="text-xs text-compumaq-textoSuave">Em aberto ({totais.qtd_em_aberto})</p>
            <p className="text-xl font-bold text-compumaq-texto" data-total="em_aberto">{brl(totais.em_aberto)}</p>
          </div>
          <div className="bg-compumaq-pretoSuave rounded-xl border border-red-300 p-4">
            <p className="text-xs text-red-600">Vencidas ({totais.qtd_vencidas})</p>
            <p className="text-xl font-bold text-red-600" data-total="vencido">{brl(totais.vencido)}</p>
          </div>
          <div className="bg-compumaq-pretoSuave rounded-xl border border-green-300 p-4">
            <p className="text-xs text-green-700">{T.pagoMes}</p>
            <p className="text-xl font-bold text-green-700" data-total="pago_mes">{brl(totais.pago_mes)}</p>
          </div>
        </div>
      )}

      <div className="bg-compumaq-pretoSuave rounded-xl border border-compumaq-dourado/25 p-4 shadow-sm flex flex-wrap gap-3 items-end">
        <div>
          <label className="block text-xs text-compumaq-textoSuave mb-1">Situacao</label>
          <select className={selectClass} aria-label="Situacao" value={filtroStatus} onChange={e => setFiltroStatus(e.target.value)}>
            <option value="em_aberto">Em aberto (inclui vencidas)</option>
            <option value="vencida">Vencidas</option>
            <option value="aberta">A vencer</option>
            <option value="paga">{T.paga}s</option>
            <option value="cancelada">Canceladas</option>
            <option value="">Todas</option>
          </select>
        </div>
        {tipo === 'receber' && (
          <div>
            <label className="block text-xs text-compumaq-textoSuave mb-1">Origem</label>
            <select className={selectClass} aria-label="Origem" value={origem} onChange={e => setOrigem(e.target.value)}>
              <option value="">Todas</option>
              <option value="anuidade">Anuidades</option>
              <option value="joia">Joias</option>
            </select>
          </div>
        )}
        <div>
          <label className="block text-xs text-compumaq-textoSuave mb-1">Vencimento de</label>
          <DataBR className={inputClass} value={de} onChange={e => setDe(e.target.value)} />
        </div>
        <div>
          <label className="block text-xs text-compumaq-textoSuave mb-1">ate</label>
          <DataBR className={inputClass} value={ate} onChange={e => setAte(e.target.value)} />
        </div>
        <div className="flex-1 min-w-[180px]">
          <label className="block text-xs text-compumaq-textoSuave mb-1">Buscar</label>
          <input className={inputClass} placeholder={`Descricao, ${T.pessoa.toLowerCase()} ou no da nota`} value={busca} onChange={e => setBusca(e.target.value)} />
        </div>
        <MenuLeiaute leiaute={leiaute} titulos={TITULO_COLUNA} onAviso={onAlert} />
        <button onClick={novo} className="px-4 py-2 bg-compumaq-dourado text-compumaq-texto text-sm rounded-lg hover:bg-compumaq-doradoClaro font-medium">{T.novo}</button>
      </div>

      <div className="bg-compumaq-pretoSuave rounded-xl border border-compumaq-dourado/25 shadow-sm overflow-x-auto">
        {carregando ? (
          <div className="flex justify-center py-12"><Spinner size="lg" /></div>
        ) : !contas.length ? (
          <p className="text-center py-12 text-compumaq-textoSuave">Nenhuma conta encontrada.</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-compumaq-preto border-b border-compumaq-dourado/25 text-xs text-compumaq-textoSuave">
              <tr>
                {leiaute.visiveis.map(id => (
                  <ColunaArrastavel key={id} id={id} leiaute={leiaute} className={`px-3 py-2 ${id === 'valor' ? 'text-right' : 'text-left'}`}>
                    {TITULO_COLUNA[id]}
                  </ColunaArrastavel>
                ))}
                <th className="text-right px-3 py-2">Acoes</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-compumaq-dourado/25">
              {contas.map(c => (
                <tr key={c.idconta} data-conta={c.idconta} className="hover:bg-compumaq-preto">
                  {leiaute.visiveis.map(id => celula(id, c))}
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    {c.situacao === 'aberta' && (
                      <button onClick={() => iniciarBaixa(c)} className="text-xs font-medium text-green-600 hover:text-green-800 mr-3">{T.baixar}</button>
                    )}
                    <button onClick={() => abrir(c)} className="text-xs font-medium text-compumaq-doradoClaro mr-3">Abrir</button>
                    {c.situacao !== 'aberta' && (
                      <button onClick={() => acao(c, 'reabrir')} className="text-xs text-compumaq-textoSuave hover:text-compumaq-texto">Reabrir</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* cadastro / edicao + anexos */}
      <Modal open={formAberto} onClose={() => setFormAberto(false)} title={editando ? `${T.titulo} — conta #${editando.idconta}` : T.novo.replace('+ ', '')} size="lg">
        <div className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2">
              <label className="block text-xs text-compumaq-textoSuave mb-1">Descricao *</label>
              <input className={inputClass} aria-label="Descricao" value={form.descricao} onChange={e => setForm({ ...form, descricao: e.target.value })} />
            </div>
            <div>
              <label className="block text-xs text-compumaq-textoSuave mb-1">{T.pessoa}</label>
              <input className={inputClass} aria-label="Pessoa" value={form.pessoa} onChange={e => setForm({ ...form, pessoa: e.target.value })} />
            </div>
            <div>
              <label className="block text-xs text-compumaq-textoSuave mb-1">No da nota / documento</label>
              <input className={inputClass} aria-label="Documento" value={form.documento} onChange={e => setForm({ ...form, documento: e.target.value })} />
            </div>
            <div>
              <label className="block text-xs text-compumaq-textoSuave mb-1">Categoria</label>
              <input className={inputClass} aria-label="Categoria" list={`cat-${tipo}`} value={form.categoria} onChange={e => setForm({ ...form, categoria: e.target.value })} />
              <datalist id={`cat-${tipo}`}>{CATEGORIAS[tipo].map(c => <option key={c} value={c} />)}</datalist>
            </div>
            <div>
              <label className="block text-xs text-compumaq-textoSuave mb-1">Valor (R$) *</label>
              <input className={inputClass} aria-label="Valor" inputMode="decimal" value={form.valor} onChange={e => setForm({ ...form, valor: e.target.value })} placeholder="0,00" />
            </div>
            <div>
              <label className="block text-xs text-compumaq-textoSuave mb-1">Vencimento *</label>
              <DataBR className={inputClass} aria-label="Vencimento" value={form.vencimento} onChange={e => setForm({ ...form, vencimento: e.target.value })} />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-xs text-compumaq-textoSuave mb-1">Observacao</label>
              <textarea className={inputClass} rows={2} value={form.observacao} onChange={e => setForm({ ...form, observacao: e.target.value })} />
            </div>
          </div>

          {editando && (
            <div className="border-t border-compumaq-dourado/25 pt-3 space-y-2" data-anexos>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="text-sm font-semibold text-compumaq-texto">Anexos (PDF, JPG, PNG ou XML, ate 10 MB)</p>
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${corStatus(editando.status)}`}>
                  {rotuloStatus(editando.status)}{editando.situacao === 'paga' ? ` em ${dataBR(editando.data_pagamento)} (${brl(editando.valor_pago)})` : ''}
                </span>
              </div>
              {anexos.length ? (
                <ul className="divide-y divide-compumaq-dourado/25 text-sm">
                  {anexos.map(a => (
                    <li key={a.idanexo} className="py-1.5 flex items-center gap-2" data-anexo={a.idanexo}>
                      <span className="text-xs px-1.5 py-0.5 rounded bg-compumaq-dourado/15 text-compumaq-doradoClaro">{TIPO_ANEXO[tipo][a.tipo]}</span>
                      <span className="flex-1 truncate text-compumaq-texto">{a.nome_original}</span>
                      <span className="text-xs text-compumaq-textoSuave">{Math.max(1, Math.round(a.tamanho / 1024))} KB</span>
                      <button onClick={() => ver(a)} className="text-xs text-compumaq-doradoClaro font-medium">{a.mime === 'application/xml' ? 'Baixar' : 'Ver'}</button>
                      <button onClick={() => excluirAnexo(a)} className="text-xs text-red-500">Excluir</button>
                    </li>
                  ))}
                </ul>
              ) : <p className="text-xs text-compumaq-textoSuave">Nenhum anexo ainda.</p>}
              <div className="flex flex-wrap items-center gap-2">
                <select className={selectClass + ' max-w-[220px]'} aria-label="Tipo do anexo" value={tipoAnexo} onChange={e => setTipoAnexo(e.target.value as any)}>
                  <option value="nota">{TIPO_ANEXO[tipo].nota}</option>
                  <option value="comprovante">{TIPO_ANEXO[tipo].comprovante}</option>
                  <option value="outro">{TIPO_ANEXO[tipo].outro}</option>
                </select>
                <label className={`px-3 py-2 border border-compumaq-dourado/50 rounded-lg text-sm cursor-pointer ${salvando ? 'opacity-50 pointer-events-none' : 'hover:bg-compumaq-preto'}`}>
                  {salvando ? 'Enviando...' : 'Anexar arquivo(s)'}
                  <input type="file" className="hidden" aria-label="Arquivo do anexo" accept={ACEITA} multiple onChange={e => { anexar(e.target.files); e.target.value = '' }} />
                </label>
              </div>
            </div>
          )}

          <div className="flex flex-wrap justify-between gap-2 pt-2 border-t border-compumaq-dourado/25">
            <div className="flex gap-2">
              {editando && editando.situacao === 'aberta' && (
                <button onClick={() => acao(editando, 'cancelar').then(() => setFormAberto(false))} className="px-3 py-2 text-xs text-compumaq-textoSuave border border-compumaq-dourado/25 rounded-lg">Cancelar conta</button>
              )}
              {editando && (
                <button onClick={() => acao(editando, 'excluir')} className="px-3 py-2 text-xs text-red-600 border border-red-300 rounded-lg">Excluir</button>
              )}
            </div>
            <div className="flex gap-2">
              <button onClick={() => setFormAberto(false)} className="px-4 py-2 text-sm border border-compumaq-dourado/25 rounded-lg">Fechar</button>
              <button onClick={salvar} disabled={salvando} className="px-4 py-2 text-sm bg-compumaq-dourado text-compumaq-texto rounded-lg font-medium disabled:opacity-50">
                {salvando ? 'Salvando...' : editando ? 'Salvar alteracoes' : 'Cadastrar'}
              </button>
            </div>
          </div>
        </div>
      </Modal>

      {/* baixa */}
      <Modal open={!!baixando} onClose={() => setBaixando(null)} title={`${T.baixar} — ${baixando?.descricao ?? ''}`} size="md">
        <div className="space-y-3">
          <p className="text-sm text-compumaq-textoSuave">Valor da conta: <b>{brl(baixando?.valor)}</b> · vencimento {dataBR(baixando?.vencimento ?? null)}</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="block text-xs text-compumaq-textoSuave mb-1">Data</label>
              <DataBR className={inputClass} aria-label="Data do pagamento" value={baixa.data_pagamento} onChange={e => setBaixa({ ...baixa, data_pagamento: e.target.value })} />
            </div>
            <div>
              <label className="block text-xs text-compumaq-textoSuave mb-1">Valor {tipo === 'pagar' ? 'pago' : 'recebido'}</label>
              <input className={inputClass} aria-label="Valor pago" inputMode="decimal" value={baixa.valor_pago} onChange={e => setBaixa({ ...baixa, valor_pago: e.target.value })} />
            </div>
            <div>
              <label className="block text-xs text-compumaq-textoSuave mb-1">Forma</label>
              <select className={selectClass} aria-label="Forma de pagamento" value={baixa.forma_pagamento} onChange={e => setBaixa({ ...baixa, forma_pagamento: e.target.value })}>
                {FORMAS.map(([v, r]) => <option key={v} value={v}>{r}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-xs text-compumaq-textoSuave mb-1">{TIPO_ANEXO[tipo].comprovante} (opcional — PDF, JPG ou PNG)</label>
            <input type="file" aria-label="Comprovante da baixa" accept={ACEITA} onChange={e => setComprovanteBaixa(e.target.files?.[0] ?? null)} className="text-sm" />
          </div>
          <div className="flex justify-end gap-2 pt-2 border-t border-compumaq-dourado/25">
            <button onClick={() => setBaixando(null)} className="px-4 py-2 text-sm border border-compumaq-dourado/25 rounded-lg">Fechar</button>
            <button onClick={confirmarBaixa} disabled={salvando} className="px-4 py-2 text-sm bg-green-600 text-white rounded-lg font-medium disabled:opacity-50">
              {salvando ? 'Salvando...' : `Confirmar ${T.baixar.toLowerCase()}`}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  )
}
